'use client';

import { Button } from '@ria-client/tw-shadcn/components/button';
import { Input } from '@ria-client/tw-shadcn/components/input';
import type { ChangeEvent } from 'react';
import { useRef, useState, useTransition } from 'react';

import { useHasWPSession } from '@/components/WordpressEditWrapper/hook';

import { revalidateAdvisorSearchCache } from '../../actions';
import { useCsvUpload } from './useCsvUpload';

export function CsvUploadSection() {
  const { hasWPSession } = useHasWPSession();
  const { status, errorMessage, upload, reset } = useCsvUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cacheCleared, setCacheCleared] = useState(false);
  const [cacheError, setCacheError] = useState<string | null>(null);
  const [isClearingCache, startClearingCache] = useTransition();

  if (!hasWPSession) return null;

  const isUploading = status === 'uploading';

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    setSelectedFile(event.target.files?.[0] ?? null);
    reset();
  }

  function handleClearCache() {
    setCacheCleared(false);
    setCacheError(null);

    startClearingCache(async () => {
      const result = await revalidateAdvisorSearchCache();

      if (result.ok) {
        setCacheCleared(true);
        return;
      }

      setCacheError(
        result.code === 'UNAUTHORIZED'
          ? 'You must be logged in to clear cache.'
          : 'Cache clear failed. Try again later.',
      );
    });
  }

  return (
    <section
      aria-labelledby="csv-upload-heading"
      className="w-full rounded-sm border border-border bg-card p-4 sm:p-5 md:ml-auto md:w-auto md:max-w-[760px]"
    >
      <div className="flex flex-col gap-4">
        <h2 id="csv-upload-heading" className="typography-text-small uppercase tracking-[0.08em]">
          Upload SEC Firm CSV
        </h2>

        <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
          <Button
            type="button"
            variant="outline"
            fullWidth={false}
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            className="h-10 min-h-10"
          >
            Choose file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            disabled={isUploading}
            onChange={handleFileChange}
            aria-label="CSV file input"
          />
          <Input
            size="small"
            value={selectedFile?.name ?? ''}
            placeholder="No file selected"
            aria-label="Selected file"
            readOnly
            className="h-10 flex-1"
          />
          <Button
            fullWidth={false}
            disabled={!selectedFile || isUploading}
            onClick={() => selectedFile && upload(selectedFile)}
            aria-label={isUploading ? 'Uploading CSV' : undefined}
            className="h-10 min-h-10"
          >
            {isUploading ? (
              // Matches the legacy MUI CircularProgress size={18}.
              <svg aria-hidden="true" className="size-[18px] animate-spin" viewBox="0 0 24 24">
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  fill="none"
                  stroke="currentColor"
                  strokeDasharray="42"
                  strokeDashoffset="12"
                  strokeWidth="3"
                />
              </svg>
            ) : (
              'Upload'
            )}
          </Button>
        </div>

        <p className="typography-text-small text-muted-foreground">
          Select a CSV file (max 30 MB) to upload.
        </p>

        <hr className="border-0 border-t border-border" />

        <div className="flex justify-stretch sm:justify-end">
          <Button
            variant="outline"
            fullWidth={false}
            disabled={isClearingCache}
            onClick={handleClearCache}
            aria-label={isClearingCache ? 'Clearing advisor search cache' : undefined}
            className="h-10 min-h-10"
          >
            {isClearingCache ? 'Clearing cache' : 'Clear advisor search cache'}
          </Button>
        </div>

        {status === 'success' ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-sm bg-accent p-4 text-accent-foreground"
          >
            Upload successful.
          </div>
        ) : null}
        {status === 'error' && errorMessage ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-sm bg-destructive/10 p-4 text-destructive"
          >
            {errorMessage}
          </div>
        ) : null}
        {cacheCleared ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-sm bg-accent p-4 text-accent-foreground"
          >
            Advisor search cache cleared.
          </div>
        ) : null}
        {cacheError ? (
          <div
            role="alert"
            aria-live="polite"
            className="rounded-sm bg-destructive/10 p-4 text-destructive"
          >
            {cacheError}
          </div>
        ) : null}
      </div>
    </section>
  );
}
